import React, { useState, useEffect, useContext } from 'react';
import { 
  Bell, Video, Users, Check, X, Trash2, 
  RefreshCw, Clock, ChevronRight, Play, Gift, User, Trophy, Wallet
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

import { API_BASE_URL } from '../config';

const API_URL = API_BASE_URL;

const NotificationsPage = ({ user, NavBar }) => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [liveActivityFeed, setLiveActivityFeed] = useState([]); // Real-time sync events
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'invites', 'live', 'activity'

  // Listen for Universal Sync events
  useEffect(() => {
    const handleProfileUpdate = (e) => {
      setLiveActivityFeed(prev => [{
        id: `sync_${Date.now()}`,
        type: 'profile_update',
        title: 'Profile Updated',
        message: `${e.detail.user_id === user?.id ? 'You' : 'Someone'} updated their profile`,
        created_at: new Date().toISOString(),
        data: e.detail
      }, ...prev.slice(0, 49)]); // Keep last 50 events
    };

    const handleNewPost = (e) => {
      setLiveActivityFeed(prev => [{
        id: `sync_${Date.now()}`,
        type: 'new_post',
        title: 'New Post',
        message: `${e.detail.user_name || 'Someone'} posted something new`,
        created_at: new Date().toISOString(),
        data: e.detail
      }, ...prev.slice(0, 49)]);
    };

    const handleWalletUpdate = (e) => {
      if (e.detail.user_id === user?.id) {
        setLiveActivityFeed(prev => [{
          id: `sync_${Date.now()}`,
          type: 'wallet_update',
          title: e.detail.change_type === 'gift_received' ? 'Gift Received!' : 'Wallet Updated',
          message: e.detail.change_type === 'gift_received' 
            ? `You received ${e.detail.amount} Rubies!` 
            : 'Your wallet balance was updated',
          created_at: new Date().toISOString(),
          data: e.detail
        }, ...prev.slice(0, 49)]);
      }
    };

    const handleBadgeUpdate = (e) => {
      setLiveActivityFeed(prev => [{
        id: `sync_${Date.now()}`,
        type: 'badge_update',
        title: 'Badge Update',
        message: e.detail.user_id === user?.id 
          ? `Your badge was updated to ${e.detail.badge_type || 'none'}!`
          : `${e.detail.user_name || 'Someone'} received a new badge!`,
        created_at: new Date().toISOString(),
        data: e.detail
      }, ...prev.slice(0, 49)]);
    };

    const handleTournamentUpdate = (e) => {
      setLiveActivityFeed(prev => [{
        id: `sync_${Date.now()}`,
        type: 'tournament_update',
        title: 'Tournament Update',
        message: 'A tournament status has changed',
        created_at: new Date().toISOString(),
        data: e.detail
      }, ...prev.slice(0, 49)]);
    };

    // Add event listeners
    window.addEventListener('sync:profile_update', handleProfileUpdate);
    window.addEventListener('sync:new_post', handleNewPost);
    window.addEventListener('sync:wallet_update', handleWalletUpdate);
    window.addEventListener('sync:badge_update', handleBadgeUpdate);
    window.addEventListener('sync:tournament_update', handleTournamentUpdate);

    return () => {
      window.removeEventListener('sync:profile_update', handleProfileUpdate);
      window.removeEventListener('sync:new_post', handleNewPost);
      window.removeEventListener('sync:wallet_update', handleWalletUpdate);
      window.removeEventListener('sync:badge_update', handleBadgeUpdate);
      window.removeEventListener('sync:tournament_update', handleTournamentUpdate);
    };
  }, [user?.id]);

  // Fetch notifications
  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/notifications`, {
        withCredentials: true
      });
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
      // Use mock data for demo
      setNotifications([
        {
          id: '1',
          type: 'who_is_live',
          title: 'Friend is Live!',
          message: 'SpadesKing just went live!',
          sender_name: 'SpadesKing',
          data: { stream_id: 'stream1', action_url: '/watch/stream1' },
          read: false,
          created_at: new Date(Date.now() - 5 * 60000).toISOString()
        },
        {
          id: '2',
          type: 'stream_share',
          title: 'Stream Invitation',
          message: 'CardShark invited you to watch Friday Night Spades',
          sender_name: 'CardShark',
          data: { stream_id: 'stream2', stream_title: 'Friday Night Spades', action_url: '/watch/stream2' },
          read: false,
          created_at: new Date(Date.now() - 30 * 60000).toISOString()
        },
        {
          id: '3',
          type: 'stream_invite',
          title: 'Live Stream Invite!',
          message: 'GamePro is inviting you to join their live stream!',
          sender_name: 'GamePro',
          data: { stream_id: 'stream3', is_live_invite: true, requires_action: true },
          read: true,
          actioned: false,
          created_at: new Date(Date.now() - 2 * 3600000).toISOString()
        }
      ]);
      setUnreadCount(2);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
    
    // Poll for new notifications every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  // Mark notification as read
  const markAsRead = async (notificationId) => {
    try {
      await axios.post(`${API_URL}/api/notifications/mark-read/${notificationId}`, {}, {
        withCredentials: true
      });
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Failed to mark as read:', err);
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      await axios.post(`${API_URL}/api/notifications/mark-all-read`, {}, {
        withCredentials: true
      });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
      toast.success('All notifications marked as read');
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    }
  };

  // Handle notification click
  const handleNotificationClick = (notification) => {
    markAsRead(notification.id);
    
    // Track notification opened
    axios.post(`${API_URL}/api/notifications/${notification.id}/track`, {
      event: 'notification_opened',
      notification_type: notification.type,
      stream_id: notification.stream_id || notification.data?.stream_id,
      user_id: user?.id,
    }, { withCredentials: true }).catch(() => {});
    
    // Deep link: go_live and stream notifications → livestream
    if (notification.type === 'go_live' || notification.type === 'who_is_live') {
      const sid = notification.stream_id || notification.data?.stream_id;
      if (sid) {
        // Track stream joined from notification
        axios.post(`${API_URL}/api/notifications/${notification.id}/track`, {
          event: 'stream_joined_from_notification',
          stream_id: sid,
          user_id: user?.id,
        }, { withCredentials: true }).catch(() => {});
        navigate(`/watch/${sid}`);
        return;
      }
    }
    
    if (notification.action_url || notification.data?.action_url) {
      navigate(notification.action_url || notification.data.action_url);
    }
  };

  // Respond to live invite
  const respondToInvite = async (notificationId, action) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/notifications/live-invites/${notificationId}/respond`,
        null,
        { params: { action }, withCredentials: true }
      );
      
      if (action === 'accept' && response.data.stream_id) {
        toast.success('Invite accepted! Joining stream...');
        navigate(`/watch/${response.data.stream_id}`);
      } else {
        toast.info('Invite declined');
      }
      
      // Update notification
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, actioned: true } : n)
      );
    } catch (err) {
      console.error('Failed to respond to invite:', err);
      toast.error('Failed to respond to invite');
    }
  };

  // Get filtered notifications
  const getFilteredNotifications = () => {
    switch (activeTab) {
      case 'invites':
        return notifications.filter(n => n.type === 'stream_share' || n.type === 'stream_invite');
      case 'live':
        return notifications.filter(n => n.type === 'who_is_live' || n.type === 'go_live');
      case 'activity':
        return liveActivityFeed;
      default:
        return notifications;
    }
  };

  // Format time ago
  const formatTimeAgo = (dateString) => {
    const now = new Date();
    const date = new Date(dateString);
    const seconds = Math.floor((now - date) / 1000);
    
    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  // Get notification icon - includes activity types
  const getNotificationIcon = (type) => {
    switch (type) {
      case 'go_live':
      case 'who_is_live':
        return <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center"><Video className="w-5 h-5 text-red-500" /></div>;
      case 'stream_share':
        return <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center"><Users className="w-5 h-5 text-emerald-500" /></div>;
      case 'stream_invite':
        return <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center"><Play className="w-5 h-5 text-purple-500" /></div>;
      case 'profile_update':
        return <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center"><User className="w-5 h-5 text-blue-500" /></div>;
      case 'new_post':
        return <div className="w-10 h-10 rounded-full bg-pink-500/20 flex items-center justify-center"><Bell className="w-5 h-5 text-pink-500" /></div>;
      case 'wallet_update':
        return <div className="w-10 h-10 rounded-full bg-yellow-500/20 flex items-center justify-center"><Wallet className="w-5 h-5 text-yellow-500" /></div>;
      case 'badge_update':
        return <div className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center"><Check className="w-5 h-5 text-cyan-500" /></div>;
      case 'tournament_update':
        return <div className="w-10 h-10 rounded-full bg-orange-500/20 flex items-center justify-center"><Trophy className="w-5 h-5 text-orange-500" /></div>;
      default:
        return <div className="w-10 h-10 rounded-full bg-gray-500/20 flex items-center justify-center"><Bell className="w-5 h-5 text-gray-500" /></div>;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-emerald-950/20 to-slate-900">
      {NavBar && <NavBar />}
      
      <div className="max-w-2xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Bell className="w-8 h-8 text-emerald-400" />
            <div>
              <h1 className="text-2xl font-bold text-white">Notifications</h1>
              {unreadCount > 0 && (
                <p className="text-emerald-400 text-sm">{unreadCount} unread</p>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchNotifications}
              className="text-gray-400 hover:text-white"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
            {unreadCount > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={markAllAsRead}
                className="text-emerald-400 border-emerald-500/50"
              >
                <Check className="w-4 h-4 mr-1" /> Mark all read
              </Button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          <Button
            variant={activeTab === 'all' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setActiveTab('all')}
            className={activeTab === 'all' ? 'bg-emerald-600' : 'text-gray-400'}
          >
            All
          </Button>
          <Button
            variant={activeTab === 'invites' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setActiveTab('invites')}
            className={activeTab === 'invites' ? 'bg-emerald-600' : 'text-gray-400'}
          >
            <Users className="w-4 h-4 mr-1" /> Invites
          </Button>
          <Button
            variant={activeTab === 'live' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setActiveTab('live')}
            className={activeTab === 'live' ? 'bg-emerald-600' : 'text-gray-400'}
          >
            <Video className="w-4 h-4 mr-1" /> Who&apos;s Live
          </Button>
          <Button
            variant={activeTab === 'activity' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setActiveTab('activity')}
            className={`relative ${activeTab === 'activity' ? 'bg-emerald-600' : 'text-gray-400'}`}
          >
            <RefreshCw className="w-4 h-4 mr-1" /> Live Activity
            {liveActivityFeed.length > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                {liveActivityFeed.length > 9 ? '9+' : liveActivityFeed.length}
              </span>
            )}
          </Button>
        </div>

        {/* Notifications List */}
        {loading && activeTab !== 'activity' ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-8 h-8 text-emerald-400 animate-spin" />
          </div>
        ) : getFilteredNotifications().length === 0 ? (
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="py-12 text-center">
              <Bell className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400 text-lg">No notifications yet</p>
              <p className="text-gray-500 text-sm mt-2">
                {activeTab === 'invites' && "When friends invite you to streams, they'll appear here"}
                {activeTab === 'live' && "When friends go live, you'll be notified here"}
                {activeTab === 'activity' && "Real-time platform activity will appear here as it happens"}
                {activeTab === 'all' && "You'll see invites, live notifications, and more here"}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {getFilteredNotifications().map((notification) => (
              <Card 
                key={notification.id}
                className={`bg-slate-800/50 border-slate-700 overflow-hidden transition-all hover:border-emerald-500/30 cursor-pointer ${
                  !notification.read ? 'border-l-4 border-l-emerald-500' : ''
                }`}
                onClick={() => handleNotificationClick(notification)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    {/* Icon */}
                    {getNotificationIcon(notification.type)}
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className={`font-medium ${notification.read ? 'text-gray-300' : 'text-white'}`}>
                            {notification.title}
                          </p>
                          <p className="text-gray-400 text-sm mt-1">{notification.message}</p>
                        </div>
                        <span className="text-xs text-gray-500 flex-shrink-0 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatTimeAgo(notification.created_at)}
                        </span>
                      </div>
                      
                      {/* Stream Title */}
                      {notification.data?.stream_title && (
                        <div className="mt-2 bg-slate-900/50 rounded-lg p-2 flex items-center gap-2">
                          <Video className="w-4 h-4 text-red-400" />
                          <span className="text-sm text-gray-300">{notification.data.stream_title}</span>
                        </div>
                      )}
                      
                      {/* Action Buttons for Live Invites */}
                      {notification.type === 'stream_invite' && notification.data?.requires_action && !notification.actioned && (
                        <div className="flex gap-2 mt-3" onClick={(e) => e.stopPropagation()}>
                          <Button
                            size="sm"
                            onClick={() => respondToInvite(notification.id, 'accept')}
                            className="bg-emerald-600 hover:bg-emerald-500"
                          >
                            <Check className="w-4 h-4 mr-1" /> Accept
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => respondToInvite(notification.id, 'decline')}
                            className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                          >
                            <X className="w-4 h-4 mr-1" /> Decline
                          </Button>
                        </div>
                      )}
                      
                      {/* Watch Now Button */}
                      {/* Round 18: the server sends live_now — no Watch button once the live has ended */}
                      {(notification.type === 'who_is_live' || notification.type === 'stream_share') && notification.live_now !== false && (
                        <Button
                          size="sm"
                          className="mt-3 bg-red-600 hover:bg-red-500"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(notification.data?.action_url || `/watch/${notification.data?.stream_id}`);
                          }}
                        >
                          <Play className="w-4 h-4 mr-1" /> Watch Now
                        </Button>
                      )}
                    </div>
                    
                    <ChevronRight className="w-5 h-5 text-gray-600 flex-shrink-0" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationsPage;
